// Estado global del Chat de Atlas.
//
// Un ÚNICO motor vive acá arriba del árbol, así el panel lateral y la pantalla dedicada
// comparten la misma conversación: si escribís en el panel y después abrís la pantalla
// completa, seguís donde estabas (mensajes, interruptores, borrador del input, adjuntos
// y el turno en curso, todo).
//
// EL MOTOR ES `useV2Chat`, el del asistente de "Crear desde cero" —
// ADR-2026-09-08-el-chat-toma-el-motor-del-asistente. Trae las herramientas con
// tarjetas (generar imagen/video/audio, elegir producto, últimas generaciones, soporte),
// la memoria escribible, el historial en `agent_chats`, la inteligencia elegible y el
// guard de créditos. Lo que el Chat le suma va como OPCIONES del hook: sus dos
// herramientas silenciosas (respuesta gráfica, sugerencias), los interruptores, el
// contexto de apertura y claves de storage propias.
//
// Cualquier pantalla abre el chat con `abrirChat({ contexto })`, donde `contexto` es una
// nota de dónde se abrió — se le pasa al agente en el bloque volátil del system prompt.
//
// ── POR QUÉ SON DOS CONTEXTOS Y NO UNO ────────────────────────────────────
// La conversación cambia de identidad en CADA token del stream. Con un solo contexto,
// todo el que lo leyera se volvía a renderizar decenas de veces por segundo — y uno de
// los lectores es `useEmpujeChat`, que lo llama `DashboardLayout`: el cascarón entero
// re-renderizando mientras Atlas escribe. Por eso la parte de APERTURA/LAYOUT (abierto,
// vista, contexto) vive en su propio contexto, que sólo cambia cuando el chat se abre,
// se cierra o cambia de vista. El contexto grande —con la conversación— lo leen
// únicamente los componentes del chat.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { conVolverA } from '../utils/volverA';
import { RUTA_CHAT_ATLAS } from './ui/rutaChatAtlas';
import { useUserGroup } from '../hooks/useUserGroup';
import { esRubroHogarDeco, esRubroModa, rubroVeMoodboards } from '../lib/rubro';
import {
  inteligenciaEfectiva,
  intelligenceToModel,
  puedeSubirInteligencia,
  type Intelligence,
} from '../pages/v2/agentModels';
import { KINDS_CON_FICHA } from '../pages/v2/puente/camposDeModo';
import {
  herramientasDePantalla,
  NOMBRE_ABRIR_MODO,
  NOMBRE_CONFIGURAR_PANTALLA,
  NOMBRE_VER_PANTALLA,
  productIdsDelInput,
  resolverModoAAbrir,
  resultadoDeAbrirModo,
  type ContextoParaAbrirModo,
} from './herramientasDePantalla';
import {
  normalizarCambios,
  resultadoDeConfigurar,
  resultadoDeVerPantalla,
  resultadoSinPantalla,
  validarCambios,
  type CambioRechazado,
  type ControladorDePantalla,
} from './puenteDePantalla';
import { usePuenteDePantalla, type PuenteDePantalla } from './PuenteDePantallaContext';
import { pantallaDeRuta, proyectoDeRuta } from './ui/pantallaDeRuta';
import type { ChatAttachment } from '../pages/v2/agentAttachments';
import {
  liberarLoteDeFotos,
  lineaDeContextoDelLote,
  registrarLoteDeFotos,
} from '../pages/v2/lotes/lotesDeFotos';
import {
  useV2Chat,
  type OpcionesV2Chat,
  type UseV2ChatReturn,
  type V2ChatMessage,
} from '../pages/v2/useV2Chat';
import { useEntornoChatAtlas } from './entornoContext';
import {
  herramientasParaInterruptores,
  NOMBRE_HERRAMIENTA_GRAFICA,
  NOMBRE_HERRAMIENTA_SUGERENCIAS,
  normalizarGrafico,
  normalizarSugerencias,
  resultadoHerramienta,
} from './herramientasChatAtlas';
import {
  guardarInteligencia,
  guardarInterruptores,
  guardarModeloDeImagen,
  guardarPermisos,
  leerInteligencia,
  leerInterruptores,
  leerModeloDeImagen,
  leerPermisos,
  PREFIJO_STORAGE_CHAT,
} from './interruptoresChat';
import type { ProveedorDeImagen } from '../lib/proveedoresDeImagen';
import { ProveedorDeImagenContext } from '../pages/v2/proveedorDeImagenContext';
import {
  accionDeTool,
  sinConfirmacion as accionSinConfirmacion,
  permisoParaNoPreguntar,
  type PermisosChat,
} from './confirmaciones';
import {
  HERRAMIENTA_CONFIGURAR_PREFERENCIAS,
  NOMBRE_CONFIGURAR_PREFERENCIAS,
  resolverConfigurarPreferencias,
} from './herramientaPreferencias';
import { contextoVolatilChatAtlas, reglasChatAtlas } from './promptChatAtlas';
import {
  HERRAMIENTA_CONECTAR_TIENDA,
  HERRAMIENTA_SUBIR_FOTOS,
  HERRAMIENTA_TRAER_DE_LA_TIENDA,
  NOMBRE_SUBIR_FOTOS,
  NOMBRE_TRAER_DE_LA_TIENDA,
  resolverSubirFotos,
  RUTA_MI_MARCA,
  resolverTraerDeLaTienda,
  type EstadoDeLaTienda,
  type ImagenGenerada,
  type ProductoDeAtlas,
} from './tiendanubeEnElChat';
import { NOMBRE_PASAR_A_1080P, resolverPasarA1080p } from './pasarA1080p';
import { olvidarAvisos } from './confirmacionVerificable';
import { pasarBorradorA1080p } from '../lib/videoBorrador';
import {
  HERRAMIENTA_CONECTAR_INSTAGRAM,
  HERRAMIENTAS_DE_INSTAGRAM,
  leyoInstagramEnLaCharla,
  NOMBRE_VER_METRICAS_DE_INSTAGRAM,
  NOMBRE_VER_POSTS_DE_INSTAGRAM,
  NOMBRE_VER_PUBLICO_DE_INSTAGRAM,
  resolverMetricasDeInstagram,
  resolverPostsDeInstagram,
  resolverPublicoDeInstagram,
  type DependenciasDeInstagram,
} from './instagramEnElChat';
import {
  estadoDeInstagram,
  EVENTO_CAMBIO_DE_INSTAGRAM,
  veInstagramOficial,
  type EstadoDeInstagram,
} from '../lib/instagramConexion';
import { consultarMetricasDeInstagram } from '../lib/instagramMetricas';
import { cuerpoDeAnuncios, HERRAMIENTA_CONECTAR_ANUNCIOS } from './anunciosEnElChat';
import {
  HERRAMIENTA_CONECTAR_MERCADOLIBRE,
  HERRAMIENTAS_DE_MERCADOLIBRE,
  NOMBRE_CAMBIAR_DATOS_EN_ML,
  NOMBRE_SUBIR_FOTOS_A_ML,
  NOMBRE_TRAER_DE_ML,
  NOMBRE_VER_CALIDAD_ML,
  NOMBRE_VER_PUBLICACIONES_ML,
  resolverCambiarDatosEnMl,
  resolverSubirFotosAMl,
  resolverTraerDeMl,
  resolverVerCalidadMl,
  resolverVerPublicaciones,
  type DepsMercadoLibre,
} from './mercadoLibreEnElChat';
import {
  estadoDeMercadoLibre,
  EVENTO_CAMBIO_DE_MERCADOLIBRE,
  veMercadoLibre,
  type EstadoDeMercadoLibre,
} from '../lib/mercadoLibreConexion';
import { importarDeMl, listarPublicacionesDeMl } from '../lib/mercadoLibreCatalogo';
import { describirPublicacionDeMl, esperarSubidaAMl, subirFotosAMl } from '../lib/mercadoLibreFotos';
import { calidadDeMl, cambiarDatosEnMl, esperarCambioEnMl, visitasDeMl } from '../lib/mercadoLibreDatos';
import {
  estadoDeAnuncios,
  EVENTO_CAMBIO_DE_ANUNCIOS,
  veAnunciosDeMeta,
  type EstadoDeAnuncios,
} from '../lib/metaAdsConexion';
import {
  fireTnExport,
  getTnProductDetails,
  getTnStatus,
  listTnProductsAll,
  listTnProductsPendientes,
  updateTnProduct,
} from '../lib/tiendaNube';
import {
  HERRAMIENTA_CREAR_PRODUCTO,
  NOMBRE_CREAR_PRODUCTO,
  resolverCrearProducto,
} from './tiendanubeCrear';
import {
  HERRAMIENTA_PUBLICAR_LANZAMIENTO,
  NOMBRE_PUBLICAR_LANZAMIENTO,
  resolverPublicarLanzamiento,
} from './tiendanubePublicar';
import {
  fetchLanzamiento,
  fetchLanzamientos,
  fetchCategorias,
  firmarFull,
} from '../pages/lanzamientos/lanzamientosApi';
import { publicarLanzamiento, publicarLanzamientoEnMl } from '../pages/lanzamientos/publicarLanzamiento';
import { publicarEnMl } from '../lib/mercadoLibrePublicar';
import {
  HERRAMIENTA_CAMBIAR_DATOS,
  HERRAMIENTA_REEMPLAZAR_FOTOS,
  NOMBRE_CAMBIAR_DATOS,
  NOMBRE_REEMPLAZAR_FOTOS,
  resolverCambiarDatos,
  resolverReemplazarFotos,
} from './tiendanubeCambios';
import {
  exportarFotosALaTienda,
  importarProductosDeLaTienda,
} from '../lib/tiendanubeOperaciones';
import { signFullUrl } from '../pages/v2/exportApi';
import { supabase } from '../lib/supabase';
import type { ContextoApertura, InterruptoresChat } from './tipos';
import {
  guardarPreferencias,
  leerPreferencias,
  normalizarPreferencias,
  type PreferenciasDeGeneracion,
} from './preferenciasDeGeneracion';
import {
  adjuntosDeLaCola,
  devolverAlBorrador,
  encolar,
  primerLoteDeLaCola,
  quitarDeCola as sacarDeCola,
  siguienteDeCola,
  type MensajeEnCola,
} from './colaDeMensajes';
import {
  alcanceDeMoodboards,
  HERRAMIENTA_VER_MOODBOARD,
  NOMBRE_VER_MOODBOARD,
  resolverVerMoodboard,
  type MoodboardResumen,
} from './verMoodboard';
import {
  cambiarColorDeMoodboard,
  crearMoodboard,
  eliminarFoto,
  fetchFotos,
  listarMoodboards,
  MOODBOARDS_BUCKET,
  renombrarMoodboard,
  subirFotoDesdeUrl,
} from '../pages/v2/moodboardsApi';
import { firmarEnLote } from '../utils/firmarEnLote';
import { ultimasImagenesAdjuntadas } from '../pages/v2/adjuntosDelMensaje';
import { liberarPdf } from '../pages/v2/pdfs/pdfsEnMemoria';
import { liberarPlanilla } from '../pages/v2/planillas/planillasEnMemoria';
import {
  HERRAMIENTA_AGREGAR_AL_MOODBOARD,
  HERRAMIENTA_EDITAR_MOODBOARD,
  NOMBRE_AGREGAR_AL_MOODBOARD,
  NOMBRE_EDITAR_MOODBOARD,
  resolverAgregarAlMoodboard,
  resolverEditarMoodboard,
} from './editarMoodboard';
import { resolverFuentesDeFoto } from './fuentesDeFoto';
import { SIN_SELECCION, tentativasDeSeleccion, type SeleccionDelLienzo } from './seleccionDelLienzo';

export type VistaChat = 'panel' | 'pantalla';

export interface OpcionesApertura {
  /** Desde dónde se abre (pantalla, ruta, qué está mirando). */
  contexto?: ContextoApertura | null;
  /** Panel lateral (default) o pantalla dedicada. */
  vista?: VistaChat;
  /**
   * Texto para dejar precargado en el input. NO se envía solo.
   *
   * Acá vivió unas horas un `enviar?: boolean` para que la caja de la home mandara el
   * turno al abrir (ADR-2026-09-20-la-caja-de-la-home-manda-el-turno). Se fue con
   * ADR-2026-09-21-la-caja-de-la-home-es-la-del-agente: esa caja pasó a ser la misma
   * `BarraEntradaChat` del chat, que llama a `enviarMensaje` derecho y no necesita
   * pedirle a la apertura que mande por ella.
   */
  mensajeInicial?: string;
}

/** Cómo se llegó a la pantalla dedicada. Los dos caminos son distintos:
 *   • expandir el panel con el botón de maximizar conserva el contexto de apertura (el
 *     agente tiene que seguir sabiendo qué es "esto") y deja el panel abierto para la
 *     vuelta;
 *   • entrar por la sidebar o por la URL no tiene ningún "acá" del que hablar, y
 *     tampoco había un panel abierto — así que no se abre uno. */
export interface EntradaAPantalla {
  conservarContexto: boolean;
}

/** La parte del estado que mira el LAYOUT. Cambia poco y a propósito: es lo que evita
 *  que el cascarón se re-renderice con cada token. */
export interface CascaronChatAtlas {
  abierto: boolean;
  vista: VistaChat;
  contexto: ContextoApertura | null;
  abrirChat: (opciones?: OpcionesApertura) => void;
  cerrarChat: () => void;
  cambiarVista: (vista: VistaChat) => void;
  /** Llegada a `/chat`. Ver `EntradaAPantalla`. */
  entrarAPantalla: (entrada: EntradaAPantalla) => void;
}

export interface ValorChatAtlas extends CascaronChatAtlas {
  // ── entrada ──────────────────────────────────────────────────────────
  /** Borrador del input, compartido entre panel y pantalla completa. */
  borrador: string;
  setBorrador: (texto: string) => void;
  /** Adjuntos enganchados y todavía sin mandar (los del motor): imagen subida, archivo
   *  de texto, producto o modelo. Se consumen al enviar. */
  adjuntos: ChatAttachment[];
  agregarAdjunto: (a: ChatAttachment) => void;
  quitarAdjunto: (id: string) => void;
  /**
   * Lo seleccionado en el lienzo que todavía no está en la caja: se dibuja apagado, sin
   * X, y pasa a `adjuntos` en cuanto el usuario escribe
   * (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir).
   */
  adjuntosTentativos: ChatAttachment[];
  /** Los ids de las celdas seleccionadas en el lienzo (para el `describir` de la pantalla). */
  seleccionDelLienzo: string[];
  /** El lienzo informa su selección cada vez que cambia, y la vacía al desmontarse. */
  fijarSeleccionDelLienzo: (s: SeleccionDelLienzo) => void;
  /**
   * El LOTE de fotos de productos enganchado (ADR-2026-09-08-el-chat-trabaja-en-lote).
   * Uno solo; los archivos viven en el registro en memoria (`lotesDeFotos`) y al modelo
   * le llega una línea de contexto con el `lote_id`. Se consume al enviar: la tarjeta de
   * `agregar_productos` los toma de ahí.
   */
  loteFotos: { id: string; cantidad: number } | null;
  /** Vuelve a pedir el catálogo de moodboards que va en el prompt: lo llama la
   *  biblioteca al crear, renombrar o borrar una carpeta
   *  (ADR-2026-09-13-el-agente-ve-los-moodboards). */
  refrescarMoodboards: () => void;
  agregarLoteFotos: (archivos: File[]) => void;
  quitarLoteFotos: () => void;
  // ── respuestas gráficas ──────────────────────────────────────────────
  /** Id del gráfico que se está mirando en el panel de la derecha, o null. Vive acá y
   *  no en la pantalla dedicada porque el viaje empieza en el panel lateral: ahí no hay
   *  ancho para dibujarlo, la tarjeta marca cuál abrir y recién después navega. */
  graficoActivoId: string | null;
  abrirGrafico: (id: string) => void;
  cerrarGrafico: () => void;
  // ── conversación ─────────────────────────────────────────────────────
  mensajes: V2ChatMessage[];
  streaming: boolean;
  /** Etiqueta de la herramienta server-side en curso (búsqueda web, scrapers), o null. */
  actividad: string | null;
  interruptores: InterruptoresChat;
  setInterruptor: (clave: keyof InterruptoresChat, valor: boolean) => void;
  /** Formato y calidad por defecto para imagen y video (menú "+" → Preferencias de contenido);
   *  ADR-2026-09-13-preferencias-de-generacion-en-el-menu-del-mas. */
  preferencias: PreferenciasDeGeneracion;
  setPreferencia: <K extends keyof PreferenciasDeGeneracion>(
    clave: K,
    valor: PreferenciasDeGeneracion[K],
  ) => void;
  /**
   * Nivel de inteligencia EFECTIVO: baja (Gemini Flash, gratis), media (GPT-6 Sol), alta
   * (Opus). Es el que se va a usar, no siempre el que está guardado — ver
   * `puedeSubirInteligencia` (ADR-2026-09-12-la-inteligencia-del-chat-es-de-pro-para-arriba).
   */
  inteligencia: Intelligence;
  /**
   * ¿El plan de esta cuenta deja subir la inteligencia por encima de "Baja"? Pro y Ultra.
   * Lo mira el selector para dibujar los otros dos niveles con candado en vez de
   * ofrecerlos y que después no pasara nada.
   */
  puedeSubirInteligencia: boolean;
  /** Qué puede hacer Atlas solo y qué te pregunta antes. */
  permisos: PermisosChat;
  setPermiso: <K extends keyof PermisosChat>(clave: K, valor: PermisosChat[K]) => void;
  setInteligencia: (nivel: Intelligence) => void;
  /**
   * Con qué motor de IA se generan las imágenes del chat, o null = Atlas elige. Lo pone el
   * usuario en "Opciones de modelo avanzadas", adentro del menú del "+"
   * (ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario; antes era una tuerca al lado
   * del micrófono, sólo para cuentas dev). Persistido en localStorage. Lo leen las tarjetas
   * que generan por `ProveedorDeImagenContext`.
   */
  modeloDeImagen: ProveedorDeImagen | null;
  setModeloDeImagen: (proveedor: ProveedorDeImagen | null) => void;
  /** Envía el texto (más los adjuntos enganchados) con el contexto de apertura vigente.
   *  Limpia el borrador. */
  enviarMensaje: (texto: string) => void;
  /** Mientras el agente responde: el mensaje queda EN COLA y sale solo cuando termina
   *  (ADR-2026-09-13-la-cola-de-mensajes-del-agente). `false` si no entró (llena o vacío). */
  encolarMensaje: (texto: string) => boolean;
  quitarDeCola: (indice: number) => void;
  /** Los mensajes en cola, en el orden en que van a salir (hasta `MAX_COLA`). Cada uno
   *  se lleva SUS adjuntos (ADR-2026-09-13-la-cola-lleva-sus-adjuntos). */
  cola: MensajeEnCola[];
  /** Detiene el turno. La cola NO sale sola: sus textos vuelven a la caja. */
  detener: () => void;
  /** Chat nuevo: la conversación actual queda en el historial. */
  reiniciar: () => void;
  /** Abre un chat del historial y lo deja como el actual. */
  cargarChat: (chatId: string) => Promise<void>;
  /** Créditos consumidos en el chat actual (agente + generaciones). */
  fetchConsumo: () => Promise<number>;
  /** El motor entero, para las piezas que dibujan sus mensajes y tarjetas
   *  (`MessageRow`, `ToolCardRenderer`). */
  motor: UseV2ChatReturn;
}

const ChatAtlasContext = createContext<ValorChatAtlas | undefined>(undefined);
const CascaronContext = createContext<CascaronChatAtlas | undefined>(undefined);

const nuevoId = (): string => {
  try {
    return crypto.randomUUID();
  } catch {
    return `g-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
};

/** El `localStorage` del navegador, o null donde no exista (tests, SSR). */
const storageLocal = (): Storage | null => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
};

/** Un respiro para que React vuelva a renderizar la pantalla antes de describirla. */
const tick = (ms = 60) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Con qué adjuntos sale un mensaje que el usuario manda o encola desde la caja: los de
 * verdad y, si hay texto, también las tentativas de la selección del lienzo
 * (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir: escribir las acepta).
 * `escribirBorrador` ya las promueve cuando el texto CAMBIA, pero quien escribe primero
 * y selecciona después ve el chip apagado sin que la caja vuelva a cambiar: si mandaba
 * así, la foto no viajaba. Sin tentativas devuelve la MISMA lista (el que llama compara
 * por identidad para no reescribir los chips en vano). Las tentativas ya excluyen lo que
 * está en la caja (`tentativasDeSeleccion`), así que no hay duplicados.
 */
const adjuntosAlMandar = (
  texto: string,
  enLaCaja: ChatAttachment[],
  tentativas: readonly ChatAttachment[],
): ChatAttachment[] => (texto.trim() && tentativas.length > 0 ? [...enLaCaja, ...tentativas] : enLaCaja);

/**
 * Espera a que la pantalla con esa clave se registre en el puente (tras `abrir_modo`
 * navega, la página nueva monta y registra en su primer efecto). Con timeout: si la
 * pantalla no tiene ficha o tarda, se sigue sin ella.
 */
async function esperarPantalla(
  puente: PuenteDePantalla,
  clave: string,
  timeoutMs: number,
): Promise<ControladorDePantalla | null> {
  const fin = Date.now() + timeoutMs;
  while (Date.now() < fin) {
    const c = puente.actual();
    if (c && c.clave === clave) return c;
    await tick(100);
  }
  return null;
}

export const ChatAtlasProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const entorno = useEntornoChatAtlas();

  // ── El puente con la pantalla (ADR-2026-09-10-el-agente-toca-la-pantalla) ──
  // Todo por refs: las herramientas se resuelven desde el motor, fuera del render.
  const puente = usePuenteDePantalla();
  const navigate = useNavigate();
  const location = useLocation();
  const rutaRef = useRef(location.pathname);
  rutaRef.current = location.pathname;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // Rubro y marca activa salen del ENTORNO y no de `useRubroMarcaActiva` directo: el
  // provider ya llama a ese hook y así el Chat tiene UNA sola noción de marca activa
  // (ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat). Antes eran dos instancias del
  // mismo hook —dos tandas de consultas— y, peor, el entorno resolvía la marca por su
  // cuenta y mal, así que el alta de producto y los moodboards no hablaban de la misma.
  const { rubro, brandId: marcaId } = entorno;
  const { isDevTester } = useUserGroup();
  const ctxModoRef = useRef<ContextoParaAbrirModo>({ esHogarDeco: false, esModa: false, isDevTester: false, plan: null });
  ctxModoRef.current = {
    esHogarDeco: esRubroHogarDeco(rubro),
    esModa: esRubroModa(rubro),
    isDevTester: isDevTester === true,
    plan: entorno.plan || null,
  };
  const productosRef = useRef(entorno.productos);
  productosRef.current = entorno.productos;
  // Los modelos y el usuario los necesita `agregar_al_moodboard` para resolver de dónde
  // sale cada foto (ADR-2026-09-13-el-agente-arma-moodboards).
  const modelosRef = useRef(entorno.modelos);
  modelosRef.current = entorno.modelos;
  const usuarioIdRef = useRef(entorno.usuarioId);
  usuarioIdRef.current = entorno.usuarioId;
  const companyIdRef = useRef(entorno.companyId);
  companyIdRef.current = entorno.companyId;
  const rubroRef = useRef(rubro);
  rubroRef.current = rubro;
  const marcaIdRef = useRef(marcaId);
  marcaIdRef.current = marcaId;

  // ── Los moodboards (ADR-2026-09-13-el-agente-ve-los-moodboards) ──
  // El catálogo (nombre y cuántas fotos) va en el bloque volátil del prompt, sólo para
  // Moda. Se pide al arrancar y cuando la biblioteca cambia una carpeta; la tool
  // `ver_moodboard` pide en vivo, no lee este cache.
  const moodboardsRef = useRef<MoodboardResumen[]>([]);
  // De qué (empresa, marca) es el catálogo que hay en el ref. Un pedido que llega tarde
  // —el de la marca anterior, o uno de Moda cuando el usuario ya se pasó a un rubro que
  // no ve moodboards— se DESCARTA en vez de pisar el ref: si no, el prompt anuncia
  // carpetas de otra marca, o carpetas que el agente no puede abrir (la tool no viaja).
  const alcanceMoodboardsRef = useRef<string | null>(null);
  const guardarMoodboards = useCallback((alcance: string | null, lista: MoodboardResumen[]) => {
    if (alcance === null || alcanceMoodboardsRef.current !== alcance) return;
    moodboardsRef.current = lista;
  }, []);
  const refrescarMoodboards = useCallback(() => {
    const companyId = entorno.companyId;
    const alcance = alcanceDeMoodboards({
      companyId,
      brandId: marcaId,
      veMoodboards: rubroVeMoodboards(rubro),
    });
    // El catálogo viejo se tira SIEMPRE al cambiar de alcance: mientras llega el nuevo, el
    // prompt no anuncia nada, que es mejor que anunciar lo de la marca anterior.
    if (alcanceMoodboardsRef.current !== alcance) moodboardsRef.current = [];
    alcanceMoodboardsRef.current = alcance;
    if (!alcance || !companyId) return;
    void listarMoodboards({ companyId, brandId: marcaId })
      .then((lista) => guardarMoodboards(alcance, lista))
      .catch((e) => console.warn('[ChatAtlas] moodboards', e));
  }, [entorno.companyId, rubro, marcaId, guardarMoodboards]);

  // ── La tienda del comerciante (ADR-2026-09-18-tiendanube-en-el-chat) ──
  // Sólo si HAY tienda conectada viajan las herramientas de Tiendanube y aparece su
  // bloque en el prompt. Se pregunta al arrancar y al cambiar de marca: `getTnStatus`
  // lee NUESTRA base, no la API de Tiendanube — el catálogo de allá se lee recién
  // cuando la persona lo pide, que es lo que le prometimos a Tiendanube en la
  // homologación.
  const tiendaRef = useRef<EstadoDeLaTienda | null>(null);
  // De qué marca es lo que hay en el ref. Una respuesta que llega tarde —la de la marca
  // anterior— se descarta en vez de pisarlo: si no, el prompt anuncia una tienda que
  // esta marca no tiene y el agente ofrece traer productos de otro lado.
  const alcanceTiendaRef = useRef<string | null>(null);
  useEffect(() => {
    const alcance = entorno.companyId ? `${entorno.companyId}:${marcaId ?? ''}` : null;
    if (alcanceTiendaRef.current !== alcance) tiendaRef.current = null;
    alcanceTiendaRef.current = alcance;
    if (!alcance) return;
    const mirar = () => {
      void getTnStatus(marcaId)
        .then((st) => {
          if (alcanceTiendaRef.current !== alcance) return;
          tiendaRef.current = { conectada: st.connected, nombre: st.storeName, error: st.error };
        })
        .catch((e) => console.warn('[ChatAtlas] tienda', e));
    };
    mirar();

    // Conectar la tienda NO cambia la empresa ni la marca, así que sin esto el efecto no
    // vuelve a correr y el agente sigue creyendo que no hay tienda aunque el usuario
    // acabe de conectarla — que es exactamente lo que pasó probándolo.
    //
    // El popup del OAuth le avisa a la ventana que lo abrió con un `postMessage`: es el
    // mismo aviso que ya espera `connectTnEnVentana`. Escucharlo acá cuesta nada y pone
    // el estado al día en el momento. La consulta lee nuestra base, no la API de ellos.
    const alConectar = (e: MessageEvent) => {
      const d = e.data as { type?: string } | null;
      if (d?.type === 'tn-connected' && e.origin === window.location.origin) mirar();
    };
    window.addEventListener('message', alConectar);
    return () => window.removeEventListener('message', alConectar);
  }, [entorno.companyId, marcaId]);

  // ── El Instagram de la marca por la API oficial (ADR-2026-09-24-instagram-en-el-chat) ──
  // Mismo criterio que la tienda: las herramientas viajan sólo si esta marca tiene su
  // Instagram conectado. Hasta que Meta apruebe el acceso Avanzado sólo pueden conectar
  // los evaluadores, así que al resto ni se le pregunta: cero llamadas nuevas para ellos.
  const instagramRef = useRef<EstadoDeInstagram | null>(null);
  const alcanceInstagramRef = useRef<string | null>(null);
  // Si en esta sesión se lo vio conectado: para avisarle al agente cuando se desconecta.
  const instagramVistoConectadoRef = useRef(false);
  const miraInstagram = veInstagramOficial(isDevTester);
  useEffect(() => {
    const alcance = miraInstagram && entorno.companyId ? `${entorno.companyId}:${marcaId ?? ''}` : null;
    if (alcanceInstagramRef.current !== alcance) {
      instagramRef.current = null;
      instagramVistoConectadoRef.current = false;
    }
    alcanceInstagramRef.current = alcance;
    if (!alcance) return;
    const mirar = () => {
      void estadoDeInstagram(marcaId).then((st) => {
        // Una respuesta que llega tarde (la de la marca anterior) no pisa la de ahora.
        if (alcanceInstagramRef.current !== alcance) return;
        instagramRef.current = st;
        if (st.conectada) instagramVistoConectadoRef.current = true;
      });
    };
    mirar();
    // Conectar o desconectar no cambia la marca: sin este aviso el agente no se entera.
    // Y si lo hizo en OTRA pestaña (o desde Instagram), el aviso no llega a esta: se
    // vuelve a mirar cuando la persona vuelve a la pestaña. Lee nuestra base, no Meta.
    const alVolver = () => {
      if (document.visibilityState === 'visible') mirar();
    };
    window.addEventListener(EVENTO_CAMBIO_DE_INSTAGRAM, mirar);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.removeEventListener(EVENTO_CAMBIO_DE_INSTAGRAM, mirar);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [entorno.companyId, marcaId, miraInstagram]);

  // ── Los anuncios de Meta de la marca (ADR-2026-09-24-anuncios-de-meta-en-el-chat) ──
  // Mismo criterio que Instagram: sólo para quien puede conectarlos (dev testers hasta la
  // aprobación de Meta). Las herramientas no son del front: el backend adjunta el MCP de
  // Meta cuando el cuerpo trae `meta_ads` (ver `cuerpoExtra`).
  const anunciosRef = useRef<EstadoDeAnuncios | null>(null);
  const alcanceAnunciosRef = useRef<string | null>(null);
  const anunciosVistosConectadosRef = useRef(false);
  const lecturaAnunciosRef = useRef(0);
  const miraAnuncios = veAnunciosDeMeta(isDevTester);
  useEffect(() => {
    const alcance = miraAnuncios && entorno.companyId ? `${entorno.companyId}:${marcaId ?? ''}` : null;
    if (alcanceAnunciosRef.current !== alcance) {
      anunciosRef.current = null;
      anunciosVistosConectadosRef.current = false;
    }
    alcanceAnunciosRef.current = alcance;
    if (!alcance) return;
    // Cada lectura lleva un número: si dos se cruzan (una empezada antes de desconectar
    // que llega después de la nueva), sólo vale la última pedida (review Greptile #143).
    const mirar = () => {
      const esta = ++lecturaAnunciosRef.current;
      void estadoDeAnuncios(marcaId).then((st) => {
        if (alcanceAnunciosRef.current !== alcance || lecturaAnunciosRef.current !== esta) return;
        anunciosRef.current = st;
        if (st.conectada) anunciosVistosConectadosRef.current = true;
      });
    };
    mirar();
    // Conectar/desconectar en esta pestaña avisa; en otra, se mira al volver. Además el
    // backend puede marcarla vencida a mitad de charla: volver a la pestaña la relee.
    const alVolver = () => {
      if (document.visibilityState === 'visible') mirar();
    };
    window.addEventListener(EVENTO_CAMBIO_DE_ANUNCIOS, mirar);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.removeEventListener(EVENTO_CAMBIO_DE_ANUNCIOS, mirar);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [entorno.companyId, marcaId, miraAnuncios]);
  // ── Mercado Libre de la marca (ADR-2026-09-28-mercadolibre-en-el-chat) ──
  // Mismo criterio que Instagram: sólo para dev testers mientras se prueba, y sólo si el
  // backend tiene la integración prendida (si no, el estado vuelve "no disponible").
  const mercadolibreRef = useRef<EstadoDeMercadoLibre | null>(null);
  const alcanceMercadolibreRef = useRef<string | null>(null);
  const mercadolibreVistoConectadoRef = useRef(false);
  const lecturaMercadolibreRef = useRef(0);
  const miraMercadolibre = veMercadoLibre(isDevTester);
  useEffect(() => {
    const alcance = miraMercadolibre && entorno.companyId ? `${entorno.companyId}:${marcaId ?? ''}` : null;
    if (alcanceMercadolibreRef.current !== alcance) {
      mercadolibreRef.current = null;
      mercadolibreVistoConectadoRef.current = false;
    }
    alcanceMercadolibreRef.current = alcance;
    if (!alcance) return;
    // Sólo vale la última lectura pedida (dos que se cruzan no se pisan al revés).
    const mirar = () => {
      const esta = ++lecturaMercadolibreRef.current;
      void estadoDeMercadoLibre(marcaId).then((st) => {
        if (alcanceMercadolibreRef.current !== alcance || lecturaMercadolibreRef.current !== esta) return;
        mercadolibreRef.current = st;
        if (st.conectada) mercadolibreVistoConectadoRef.current = true;
      });
    };
    mirar();
    const alVolver = () => {
      if (document.visibilityState === 'visible') mirar();
    };
    window.addEventListener(EVENTO_CAMBIO_DE_MERCADOLIBRE, mirar);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.removeEventListener(EVENTO_CAMBIO_DE_MERCADOLIBRE, mirar);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [entorno.companyId, marcaId, miraMercadolibre]);

  /** Lo que necesitan las herramientas de Mercado Libre: todo contra el backend, con la marca de AHORA. */
  const depsDeMercadoLibre = useCallback(
    (): DepsMercadoLibre => ({
      listar: (estado, agrupar) => listarPublicacionesDeMl(marcaIdRef.current, estado, agrupar),
      importar: (elegidas) => importarDeMl(marcaIdRef.current, elegidas),
      describir: (itemId) => describirPublicacionDeMl(marcaIdRef.current, itemId),
      subir: (p) => subirFotosAMl(marcaIdRef.current, { ...p, forzarPortada: false }),
      esperarSubida: (id) => esperarSubidaAMl(id),
      cambiar: (p) => cambiarDatosEnMl(p),
      esperarCambio: (id) => esperarCambioEnMl(id),
      calidad: (itemId) => calidadDeMl(itemId),
      visitas: (itemId) => visitasDeMl(itemId),
    }),
    [],
  );

  /** El nivel efectivo del turno (se llena más abajo, donde se calcula). */
  const inteligenciaRef = useRef<Intelligence>('media');

  /** Lo que necesitan las herramientas de Instagram: la marca de AHORA y qué hacer si venció. */
  const depsDeInstagram = useCallback(
    (): DependenciasDeInstagram => ({
      consultar: (pedido) => consultarMetricasDeInstagram(marcaIdRef.current, pedido),
      // En el próximo turno las herramientas dejan de viajar y el bloque pide reconectar.
      alVencer: () => {
        if (instagramRef.current) {
          instagramRef.current = { ...instagramRef.current, conectada: false, estado: 'vencida' };
        }
      },
    }),
    [],
  );

  // Red de seguridad: al SALIR de Mi marca —que es donde se conecta— se vuelve a mirar,
  // por si el aviso del popup se perdió. Sólo si hasta ahora no había tienda: al que ya
  // la tiene no hace falta volver a preguntarle en cada paso.
  const rutaAnteriorRef = useRef<string | null>(null);
  useEffect(() => {
    const anterior = rutaAnteriorRef.current;
    const ahora = location.pathname;
    rutaAnteriorRef.current = ahora;
    if (anterior === RUTA_MI_MARCA && ahora !== RUTA_MI_MARCA && !tiendaRef.current?.conectada) {
      void getTnStatus(marcaIdRef.current)
        .then((st) => {
          tiendaRef.current = { conectada: st.connected, nombre: st.storeName, error: st.error };
        })
        .catch(() => {
          /* si falla se queda como estaba: no se anuncia una tienda que no se pudo ver */
        });
    }
  }, [location.pathname]);

  /**
   * Cuántas veces habló la PERSONA en esta conversación. Lo que habilita las acciones que
   * no se deshacen: el modelo puede decir "confirmado", pero si este número no creció
   * desde el aviso, nadie dijo que sí (ADR-2026-09-17-flujos-el-permiso-para-gastar-no-lo-declara-el-modelo).
   * Se escribe abajo, apenas el motor existe, igual que en el chat del canvas de Flujos.
   */
  const turnoRef = useRef(0);
  const ultimoMensajeRef = useRef('');

  /** Las imágenes generadas pedidas, con su archivo y a qué producto de la tienda fueron. */
  const imagenesPorId = useCallback(async (ids: string[]): Promise<ImagenGenerada[]> => {
    const companyId = companyIdRef.current;
    if (!companyId || ids.length === 0) return [];
    const { data } = await supabase
      .from('imagenes_generadas')
      .select('id, file_name, tn_product_id')
      .eq('id_company', companyId)
      .in('id', ids);
    return ((data ?? []) as { id: string; file_name: string | null; tn_product_id: number | null }[])
      .filter((r) => !!r.file_name)
      .map(
        (r): ImagenGenerada => ({
          id: r.id,
          fileName: r.file_name as string,
          tnProductId: r.tn_product_id != null ? Number(r.tn_product_id) : null,
        }),
      );
  }, []);

  /**
   * Los productos de la marca con su vínculo a la tienda, y con las variantes que les
   * cuelgan. Las variantes viajan porque son la ÚNICA forma de cruzar los dos mundos:
   * el catálogo del chat, `pick_product` y los adjuntos hablan de variantes, y el
   * vínculo con la tienda es del producto
   * (ADR-2026-09-21-el-chat-habla-de-variantes-y-la-tienda-de-productos).
   *
   * El filtro por marca es el MISMO que usa el catálogo del chat
   * (`productsApi.fetchUserProducts`): la marca activa **o sin marca**. Con el `.eq`
   * que había, un producto sin marca —los que quedaron del onboarding viejo— aparecía
   * en el catálogo del agente pero no acá, y la herramienta contestaba "no lo encontré"
   * por algo que la persona estaba viendo en la pantalla.
   */
  const productosConVinculo = useCallback(async (): Promise<ProductoDeAtlas[]> => {
    const companyId = companyIdRef.current;
    if (!companyId) return [];
    let q = supabase
      .from('productos')
      .select('id, nombre, tn_product_id, variantes(id, nombre, activo)')
      .eq('id_company', companyId);
    if (marcaIdRef.current) q = q.or(`brand_id.eq.${marcaIdRef.current},brand_id.is.null`);
    const { data } = await q;
    type FilaDeProducto = {
      id: string;
      nombre: string;
      tn_product_id: number | null;
      variantes?: { id: string; nombre: string | null; activo: boolean | null }[] | null;
    };
    return ((data ?? []) as FilaDeProducto[]).map(
      (p): ProductoDeAtlas => ({
        id: p.id,
        nombre: p.nombre,
        tnProductId: p.tn_product_id != null ? Number(p.tn_product_id) : null,
        // Sólo las activas, que son las que el catálogo del chat muestra: una variante
        // dada de baja no es algo que la persona pueda estar nombrando.
        variantes: (p.variantes ?? [])
          .filter((v) => v.activo !== false && typeof v.nombre === 'string')
          .map((v) => ({ id: v.id, nombre: v.nombre as string })),
      }),
    );
  }, []);

  // Por ref: las herramientas se arman UNA vez (el `useMemo` de `opciones`) y este
  // callback cambia de identidad con la marca.
  const refrescarMoodboardsRef = useRef(refrescarMoodboards);
  refrescarMoodboardsRef.current = refrescarMoodboards;

  /**
   * El catálogo de carpetas, EN VIVO, para las tres herramientas de moodboard. El alcance
   * se congela ANTES del pedido: la tool devuelve lo que trajo (es lo que el modelo
   * preguntó), pero el cache del prompt sólo se actualiza si la marca sigue siendo la
   * misma cuando vuelve.
   */
  const listarCarpetas = useCallback(async (): Promise<MoodboardResumen[]> => {
    const companyId = companyIdRef.current;
    if (!companyId) return [];
    const alcance = alcanceDeMoodboards({
      companyId,
      brandId: marcaIdRef.current,
      veMoodboards: rubroVeMoodboards(rubroRef.current),
    });
    const lista = await listarMoodboards({ companyId, brandId: marcaIdRef.current });
    guardarMoodboards(alcance, lista);
    return lista;
  }, [guardarMoodboards]);
  useEffect(() => {
    refrescarMoodboards();
  }, [refrescarMoodboards]);

  const [abierto, setAbierto] = useState(false);
  const [vista, setVista] = useState<VistaChat>('panel');
  const [contexto, setContexto] = useState<ContextoApertura | null>(null);
  const [borrador, setBorrador] = useState('');
  const [graficoActivoId, setGraficoActivoId] = useState<string | null>(null);
  const [interruptores, setInterruptores] = useState<InterruptoresChat>(() =>
    leerInterruptores(storageLocal()),
  );
  const [preferencias, setPreferencias] = useState<PreferenciasDeGeneracion>(() =>
    leerPreferencias(storageLocal()),
  );
  const preferenciasRef = useRef<PreferenciasDeGeneracion>(preferencias);
  preferenciasRef.current = preferencias;
  const [inteligencia, setInteligenciaState] = useState<Intelligence>(() =>
    leerInteligencia(storageLocal()),
  );
  // El motor de imagen que eligió el usuario en "Opciones de modelo avanzadas", o null =
  // Atlas elige (ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario).
  const [modeloDeImagen, setModeloDeImagenState] = useState<ProveedorDeImagen | null>(() =>
    leerModeloDeImagen(storageLocal()),
  );
  // Qué puede hacer Atlas solo (ADR-2026-09-08-permisos-de-generacion-del-chat,
  // ADR-2026-09-08-permisos-tambien-para-el-catalogo).
  const [permisos, setPermisosState] = useState<PermisosChat>(() => leerPermisos(storageLocal()));

  // Espejos para que las opciones del motor —que se leen en cada envío— vean el estado
  // vigente sin recrearse.
  const contextoRef = useRef<ContextoApertura | null>(null);
  contextoRef.current = contexto;
  const interruptoresRef = useRef<InterruptoresChat>(interruptores);
  interruptoresRef.current = interruptores;
  const permisosRef = useRef<PermisosChat>(permisos);
  permisosRef.current = permisos;
  // Los dos setters que el motor necesita llamar (el botón blanco de la card y la tool
  // `configurar_preferencias`) se definen DESPUÉS que `opciones`, así que entran por ref
  // como todo lo demás. Se les asigna el valor real apenas existen.
  const setPermisoRef = useRef<
    <K extends keyof PermisosChat>(clave: K, valor: PermisosChat[K]) => void
  >(() => {});
  const setPreferenciaRef = useRef<
    <K extends keyof PreferenciasDeGeneracion>(
      clave: K,
      valor: PreferenciasDeGeneracion[K],
    ) => void
  >(() => {});

  // El lote de fotos enganchado. `loteRef` es para las opciones del motor, que leen en
  // cada envío; el estado es para el chip.
  const [loteFotos, setLoteFotos] = useState<{ id: string; cantidad: number } | null>(null);
  // La cola de mensajes (ADR-2026-09-13-la-cola-de-mensajes-del-agente). El ref es el
  // espejo sincrónico: encolar tiene que CONTESTAR si entró, y un updater de estado no
  // puede decirlo en el acto.
  const [cola, setCola] = useState<MensajeEnCola[]>([]);
  const colaRef = useRef<MensajeEnCola[]>([]);
  colaRef.current = cola;
  const loteRef = useRef<{ id: string; cantidad: number } | null>(null);
  loteRef.current = loteFotos;
  const agregarLoteFotos = useCallback((archivos: File[]) => {
    if (archivos.length === 0) return;
    // Uno solo: el anterior, si había, se libera.
    if (loteRef.current) liberarLoteDeFotos(loteRef.current.id);
    const lote = registrarLoteDeFotos(archivos);
    setLoteFotos({ id: lote.id, cantidad: lote.archivos.length });
  }, []);
  const quitarLoteFotos = useCallback(() => {
    if (loteRef.current) liberarLoteDeFotos(loteRef.current.id);
    setLoteFotos(null);
  }, []);

  // Las opciones se arman UNA vez: todo lo que cambia entra por los refs.
  const opciones = useMemo<OpcionesV2Chat>(
    () => ({
      prefijoStorage: PREFIJO_STORAGE_CHAT,
      // Las dos herramientas del Chat. La gráfica sólo si el interruptor está prendido:
      // apagado ⇒ ni viaja, así el modelo no puede intentar usarla.
      herramientasExtra: () => [
        ...herramientasParaInterruptores(interruptoresRef.current),
        // Sólo Moda tiene moodboards: para el resto la tool ni viaja.
        ...(rubroVeMoodboards(rubroRef.current)
          ? [
              HERRAMIENTA_VER_MOODBOARD,
              HERRAMIENTA_AGREGAR_AL_MOODBOARD,
              HERRAMIENTA_EDITAR_MOODBOARD,
            ]
          : []),
        // Las de pantalla, siempre: ver/configurar la pantalla abierta y abrir un modo.
        ...herramientasDePantalla(ctxModoRef.current),
        // Las PROPIAS de la pantalla montada (el Estudio de Video registra las suyas):
        // viajan sólo mientras el usuario está ahí
        // (ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente).
        ...(puente.actual()?.herramientas ?? []),
        // Las de Tiendanube: viajan SÓLO si esta marca tiene tienda conectada
        // (ADR-2026-09-18-tiendanube-en-el-chat). Mismo criterio que los moodboards:
        // no se le cobra el prefijo de cada turno a quien no puede usarlas.
        // Con tienda conectada viajan las que trabajan; sin ella, sólo la que lleva a
        // conectarla. Nunca las dos cosas: el que ya la tiene no necesita que le
        // ofrezcan conectarla, y el que no la tiene no puede usar ninguna de las otras
        // (ADR-2026-09-18-tiendanube-en-el-chat).
        ...(tiendaRef.current?.conectada
          ? [
              HERRAMIENTA_TRAER_DE_LA_TIENDA,
              HERRAMIENTA_SUBIR_FOTOS,
              HERRAMIENTA_CREAR_PRODUCTO,
              HERRAMIENTA_REEMPLAZAR_FOTOS,
              HERRAMIENTA_CAMBIAR_DATOS,
            ]
          : [HERRAMIENTA_CONECTAR_TIENDA]),
        // Las de Instagram: con la cuenta de la marca conectada viajan las que leen; sin
        // ella (o vencida), sólo la tarjeta para conectarla. Nunca las dos. El ref sólo se
        // llena para quien ve Instagram oficial: al resto no le viaja ninguna
        // (ADR-2026-09-24-instagram-en-el-chat, ADR-2026-09-24-instagram-boton-en-mi-marca).
        ...(instagramRef.current && !instagramRef.current.error
          ? instagramRef.current.conectada
            ? HERRAMIENTAS_DE_INSTAGRAM
            : [HERRAMIENTA_CONECTAR_INSTAGRAM]
          : []),
        // Anuncios de Meta: sólo la tarjeta para conectarlos, conectados o no (las
        // `ads_…` las suma el backend; si la llave vence a mitad de charla, el agente
        // tiene con qué mandarlo a reconectar).
        ...(anunciosRef.current && !anunciosRef.current.error ? [HERRAMIENTA_CONECTAR_ANUNCIOS] : []),
        // Mercado Libre: con la cuenta conectada, sus herramientas; sin ella (o a reconectar),
        // sólo la tarjeta. Apagado en el backend o sin dev tester: nada.
        ...(mercadolibreRef.current && !mercadolibreRef.current.error && mercadolibreRef.current.disponible
          ? mercadolibreRef.current.conectada
            ? HERRAMIENTAS_DE_MERCADOLIBRE
            : [HERRAMIENTA_CONECTAR_MERCADOLIBRE]
          : []),
        // Publicar un lanzamiento: con Tiendanube o con Mercado Libre conectado (publica en las
        // tiendas que eligió el lanzamiento — ADR-2026-09-30-publicar-en-mercado-libre-desde-el-chat).
        // Una sola herramienta para las dos: dos casi iguales son las que el modelo confunde.
        ...(tiendaRef.current?.conectada ||
        (mercadolibreRef.current && !mercadolibreRef.current.error && mercadolibreRef.current.disponible && mercadolibreRef.current.conectada)
          ? [HERRAMIENTA_PUBLICAR_LANZAMIENTO]
          : []),
        // Cambiar el formato/calidad por defecto hablando. Es del CHAT y no de
        // `V2_AGENT_TOOLS` porque las preferencias son del menú "+" del Chat: el
        // asistente legacy ni las lee (ADR-2026-09-13-el-agente-configura-las-preferencias).
        HERRAMIENTA_CONFIGURAR_PREFERENCIAS,
      ],
      // Las preferencias de generación, frescas en cada turno: van al bloque volátil
      // del prompt y a las cards (ADR-2026-09-13-preferencias-de-generacion-en-el-menu-del-mas).
      preferencias: () => preferenciasRef.current,
      // Cambiar el formato o la calidad en una card deja ese valor como DEFAULT
      // (ADR-2026-09-14-cambiar-el-formato-en-la-card-cambia-el-default). Va por ref
      // porque `setPreferencia` se define más abajo que estas opciones.
      recordarPreferencia: (clave, valor) => setPreferenciaRef.current(clave, valor),
      // El botón blanco de la card: ejecuta la acción y deja el permiso puesto
      // (ADR-2026-09-13-generar-sin-preguntarme-en-la-card). Viaja por acá y no por el
      // contexto porque `ChatPanelV2` también se monta fuera del provider, donde
      // `useChatAtlas` tira; sin esta opción la card no dibuja el botón.
      noPreguntarMas: (tipo) => {
        const { clave, valor } = permisoParaNoPreguntar(tipo);
        setPermisoRef.current(clave, valor as PermisosChat[typeof clave]);
      },
      // Una herramienta propia de la pantalla la ejecuta la pantalla. Se resuelve por
      // nombre en cada llamada: si el usuario se fue del editor a mitad de turno, el
      // nombre deja de existir y el modelo recibe el motivo en vez de un cambio fantasma.
      resolverHerramienta: (nombre) => {
        const c = puente.actual();
        if (!c?.ejecutar || !c.herramientas?.some((h) => h.name === nombre)) return null;
        const ejecutar = c.ejecutar;
        return async (input) => {
          const r = await ejecutar(nombre, input);
          if (Array.isArray(r.content)) {
            return r.is_error ? { __bloques: r.content, __error: true } : { __bloques: r.content };
          }
          return r.is_error ? { __texto: r.content, __error: true } : r.content;
        };
      },
      etiquetaDeHerramienta: (nombre) =>
        puente.actual()?.herramientas?.find((h) => h.name === nombre)?.etiqueta ?? null,
      pantalla: () => rutaRef.current,
      // Un chat que nace adentro del editor de video queda asociado a ese proyecto
      // (ADR-2026-09-11-el-chat-del-proyecto-de-video). Se lee al crear la fila.
      proyecto: () => proyectoDeRuta(rutaRef.current),
      herramientasSilenciosas: {
        // ── pasar_video_a_1080p (ADR-2026-09-27-borrador-480p-y-pasar-a-1080p). Cobra, así
        // que `confirmado: true` sólo vale si la persona contestó que sí DESPUÉS del aviso
        // (confirmación verificable); la fila se lee de ESTA empresa.
        [NOMBRE_PASAR_A_1080P]: (input) =>
          resolverPasarA1080p(input, {
            companyId: companyIdRef.current,
            leerFila: async (videoId) => {
              const { data } = await supabase
                .from('imagenes_generadas')
                .select('datos_generacion')
                .eq('id', videoId)
                .eq('id_company', companyIdRef.current ?? '')
                .maybeSingle();
              return (data as { datos_generacion: unknown } | null) ?? null;
            },
            finalizar: (videoId) => pasarBorradorA1080p({ videoId }),
          }, { turno: turnoRef.current, ultimoMensaje: ultimoMensajeRef.current }),
        // ── Instagram (ADR-2026-09-24-instagram-en-el-chat). SILENCIOSAS: sólo leen, y
        // son gratis. Si la conexión venció a mitad de charla, dejan de viajar.
        [NOMBRE_VER_METRICAS_DE_INSTAGRAM]: (input) => resolverMetricasDeInstagram(input, depsDeInstagram()),
        [NOMBRE_VER_POSTS_DE_INSTAGRAM]: (input) => resolverPostsDeInstagram(input, depsDeInstagram()),
        [NOMBRE_VER_PUBLICO_DE_INSTAGRAM]: (input) => resolverPublicoDeInstagram(input, depsDeInstagram()),
        // ── Mercado Libre (ADR-2026-09-28-mercadolibre-en-el-chat). Ver y calidad sólo leen;
        // traer, subir y cambiar piden la confirmación verificable (claves propias).
        [NOMBRE_VER_PUBLICACIONES_ML]: (input) => resolverVerPublicaciones(input, depsDeMercadoLibre()),
        [NOMBRE_VER_CALIDAD_ML]: (input) => resolverVerCalidadMl(input, depsDeMercadoLibre()),
        [NOMBRE_TRAER_DE_ML]: (input) =>
          resolverTraerDeMl(input, depsDeMercadoLibre(), { turno: turnoRef.current, ultimoMensaje: ultimoMensajeRef.current }),
        [NOMBRE_SUBIR_FOTOS_A_ML]: (input) =>
          resolverSubirFotosAMl(input, depsDeMercadoLibre(), { turno: turnoRef.current, ultimoMensaje: ultimoMensajeRef.current }),
        [NOMBRE_CAMBIAR_DATOS_EN_ML]: (input) =>
          resolverCambiarDatosEnMl(input, depsDeMercadoLibre(), { turno: turnoRef.current, ultimoMensaje: ultimoMensajeRef.current }),
        // ── traer_productos_de_la_tienda (ADR-2026-09-18-tiendanube-en-el-chat).
        // SILENCIOSA: escribe en Atlas y no en la tienda del cliente, así que no hay
        // nada que confirmar con una tarjeta — el permiso lo da la persona pidiéndolo.
        // Lo que sí hace la tool es contar cuántos son antes de traer un catálogo
        // entero, y decir siempre que los está trayendo y nunca que ya están.
        [NOMBRE_TRAER_DE_LA_TIENDA]: (input) =>
          resolverTraerDeLaTienda(input, {
            pendientes: async () => {
              const r = await listTnProductsPendientes(marcaIdRef.current);
              return { conectada: r.connected, nombre: r.storeName, productos: r.productos };
            },
            importar: (productos) =>
              importarProductosDeLaTienda(productos, {
                userId: usuarioIdRef.current ?? undefined,
                companyId: companyIdRef.current,
                brandId: marcaIdRef.current,
              }),
          },
          // Traer también pasa por la confirmación: sin esto el modelo podía recibir la
          // lista con las fotos y volver a llamar en el mismo turno (review Greptile #127).
          { turno: turnoRef.current, ultimoMensaje: ultimoMensajeRef.current }),
        // ── subir_fotos_a_la_tienda (ADR-2026-09-18-tiendanube-en-el-chat).
        // SILENCIOSA con matices: AGREGA a la galería y no toca lo que el producto ya
        // tiene, así que no hay nada que se pierda; el permiso lo da la persona
        // pidiéndolo. Lo delicado —reemplazar las fotos de allá— NO existe como tool.
        // La tool se encarga de no duplicar y de no adivinar a qué producto de la
        // tienda corresponde uno cargado a mano.
        [NOMBRE_SUBIR_FOTOS]: (input) =>
          resolverSubirFotos(input, {
            productosDeAtlas: productosConVinculo,
            imagenes: imagenesPorId,
            catalogoDeLaTienda: () => listTnProductsAll(marcaIdRef.current),
            subir: async ({ productoId, tnProductId, imagenes }) => {
              const companyId = companyIdRef.current;
              if (!companyId) return { imagenes: 0 };
              return exportarFotosALaTienda({
                productos: [
                  {
                    productoId,
                    accion: { tipo: 'agregar', tnProductId },
                    // El orden es el que pidió el usuario; van al final de la galería.
                    imagenes: imagenes.map((i, n) => ({
                      id: i.id,
                      fileName: i.fileName,
                      orden: n + 1,
                    })),
                  },
                ],
                firmar: (fileName) => signFullUrl(companyId, fileName),
                userId: usuarioIdRef.current ?? undefined,
                companyId,
                brandId: marcaIdRef.current,
              });
            },
            guardarVinculo: async (productoId, tnProductId) => {
              const companyId = companyIdRef.current;
              if (!companyId) return;
              await supabase
                .from('productos')
                .update({ tn_product_id: tnProductId })
                .eq('id', productoId)
                .eq('id_company', companyId);
            },
          }),
        // ── crear_producto_en_la_tienda (ADR-2026-09-21-crear-el-producto-en-la-
        // tienda-desde-el-chat). ESCRIBE en la tienda y queda a la vista de sus
        // clientes, así que pasa por la misma confirmación verificable que la fase 3:
        // la primera llamada avisa y no crea nada.
        [NOMBRE_CREAR_PRODUCTO]: (input) =>
          resolverCrearProducto(
            input,
            {
              productosDeAtlas: productosConVinculo,
              imagenes: imagenesPorId,
              catalogoDeLaTienda: () => listTnProductsAll(marcaIdRef.current),
              crear: async ({ productoId, nuevo, imagenes }) => {
                const companyId = companyIdRef.current;
                if (!companyId) return { productos: 0, imagenes: 0 };
                return exportarFotosALaTienda({
                  productos: [
                    {
                      productoId,
                      accion: { tipo: 'crear', nuevo },
                      imagenes: imagenes.map((i, n) => ({
                        id: i.id,
                        fileName: i.fileName,
                        orden: n + 1,
                      })),
                    },
                  ],
                  firmar: (fileName) => signFullUrl(companyId, fileName),
                  userId: usuarioIdRef.current ?? undefined,
                  companyId,
                  brandId: marcaIdRef.current,
                });
              },
            },
            { turno: turnoRef.current, ultimoMensaje: ultimoMensajeRef.current },
          ),
        // ── publicar_lanzamiento (ADR-2026-09-21-publicar-un-lanzamiento-desde-el-
        // chat). La más grande: crea productos NUEVOS a la venta y las categorías que
        // falten. Los pasos son los MISMOS que los del botón de la pantalla
        // (`publicarLanzamiento`); acá sólo se elige cuál y se confirma. El árbol de
        // categorías se lee en el momento, que es lo que evita crearlas duplicadas.
        [NOMBRE_PUBLICAR_LANZAMIENTO]: (input) =>
          resolverPublicarLanzamiento(
            input,
            {
              lista: async () => {
                const companyId = companyIdRef.current;
                if (!companyId) return [];
                return fetchLanzamientos(companyId, marcaIdRef.current);
              },
              detalle: async (id) => {
                const companyId = companyIdRef.current;
                if (!companyId) return null;
                return fetchLanzamiento(id, companyId);
              },
              publicar: async (detalle) => {
                const companyId = companyIdRef.current;
                if (!companyId) return { estado: 'rechazado' as const };
                // El lanzamiento manda su marca: puede no ser la activa.
                const brandId = detalle.brandId ?? marcaIdRef.current;
                return publicarLanzamiento(detalle, {
                  estadoDeLaTienda: () => getTnStatus(brandId),
                  firmar: (fileName) => firmarFull(companyId, fileName),
                  categorias: () => fetchCategorias(companyId, brandId),
                  disparar: (productos, lanzamientoId) =>
                    fireTnExport({
                      userId: usuarioIdRef.current ?? undefined,
                      companyId,
                      brandId,
                      productos,
                      // El worker anota cómo terminó cada producto: la pantalla del lanzamiento
                      // lo ve aunque se haya publicado desde acá.
                      lanzamientoId,
                    }),
                });
              },
              // Mercado Libre, con los mismos pasos que el botón (`publicarLanzamientoEnMl`). Sólo
              // si la marca lo tiene disponible; si no, el chat no publica allá.
              mercadoLibre:
                mercadolibreRef.current && !mercadolibreRef.current.error && mercadolibreRef.current.disponible
                  ? {
                      cuentaConectada: async (detalle) => {
                        const e = await estadoDeMercadoLibre(detalle.brandId ?? marcaIdRef.current);
                        return e.disponible && e.conectada;
                      },
                      publicar: async (detalle) => {
                        const brandId = detalle.brandId ?? marcaIdRef.current;
                        const e = await estadoDeMercadoLibre(brandId);
                        return publicarLanzamientoEnMl(detalle, {
                          cuentaConectada: e.disponible && e.conectada,
                          publicar: (productos, lanzamientoId) => publicarEnMl({ brandId, lanzamientoId, productos }),
                        });
                      },
                    }
                  : undefined,
            },
            { turno: turnoRef.current, ultimoMensaje: ultimoMensajeRef.current },
          ),
        // ── Lo que PISA o BORRA en la tienda del cliente (fase 3). Silenciosas igual,
        // pero la tarjeta no es lo que las frena: la primera llamada sólo AVISA y no
        // hace nada, y la segunda corre únicamente si la persona habló en el medio.
        // Esa comprobación la hacemos nosotros, no se le cree al modelo.
        [NOMBRE_REEMPLAZAR_FOTOS]: (input) =>
          resolverReemplazarFotos(
            input,
            {
              productosDeAtlas: productosConVinculo,
              reemplazar: async ({ productoId, tnProductId, imagenIds }) => {
                const companyId = companyIdRef.current;
                if (!companyId) return { imagenes: 0 };
                const imagenes = await imagenesPorId(imagenIds);
                if (imagenes.length === 0) return { imagenes: 0 };
                return exportarFotosALaTienda({
                  productos: [
                    {
                      productoId,
                      accion: { tipo: 'reemplazar', tnProductId },
                      imagenes: imagenes.map((i, n) => ({
                        id: i.id,
                        fileName: i.fileName,
                        orden: n + 1,
                      })),
                    },
                  ],
                  firmar: (fileName) => signFullUrl(companyId, fileName),
                  userId: usuarioIdRef.current ?? undefined,
                  companyId,
                  brandId: marcaIdRef.current,
                });
              },
            },
            { turno: turnoRef.current, ultimoMensaje: ultimoMensajeRef.current },
          ),
        [NOMBRE_CAMBIAR_DATOS]: (input) =>
          resolverCambiarDatos(
            input,
            {
              productosDeAtlas: productosConVinculo,
              datosActuales: (tnProductId) => getTnProductDetails(marcaIdRef.current, tnProductId),
              guardar: ({ tnProductId, ficha }) =>
                updateTnProduct({
                  brandId: marcaIdRef.current,
                  tnProductId,
                  varianteId: ficha.variante_id,
                  // Con una sola variante, precio/stock/SKU viven ahí; con varias, la
                  // tool ni llega hasta acá pidiendo esos campos.
                  editarVariante: ficha.variante_count <= 1,
                  nombre: ficha.nombre,
                  descripcion: ficha.descripcion,
                  precio: ficha.precio ?? '',
                  stock: ficha.stock != null ? String(ficha.stock) : '',
                  sku: ficha.sku ?? '',
                }),
            },
            { turno: turnoRef.current, ultimoMensaje: ultimoMensajeRef.current },
          ),
        // ── ver_moodboard (ADR-2026-09-13-el-agente-ve-los-moodboards): lista o mira
        // de a diez. Firma el ORIGINAL de cada foto: es la firma que se renueva antes
        // de cada turno; la miniatura con transform, no.
        [NOMBRE_VER_MOODBOARD]: (input) =>
          resolverVerMoodboard(input, {
            listar: listarCarpetas,
            fotosDe: async (moodboardId) => {
              const companyId = companyIdRef.current;
              if (!companyId) return [];
              const fotos = await fetchFotos({ companyId, moodboardId });
              const firmas = await firmarEnLote(fotos.map((f) => f.path), MOODBOARDS_BUCKET);
              return fotos.map((f) => ({ id: f.id, url: firmas.get(f.path) ?? null }));
            },
          }),
        // ── agregar_al_moodboard / editar_moodboard
        // (ADR-2026-09-13-el-agente-arma-moodboards). Escriben en la marca, pero son
        // SILENCIOSAS como `save_memory`: son reversibles desde Mi marca y una tarjeta de
        // confirmación por foto convertiría "guardá estas cinco" en cinco clics. El
        // permiso lo da el usuario hablando, y la tool le pide al agente que cuente qué
        // hizo. Lo único irreversible —borrar la carpeta entera— no existe como tool.
        [NOMBRE_AGREGAR_AL_MOODBOARD]: (input) =>
          resolverAgregarAlMoodboard(input, {
            listar: listarCarpetas,
            crear: async (nombre, color) => {
              const companyId = companyIdRef.current;
              if (!companyId) throw new Error('sin empresa');
              const m = await crearMoodboard({
                companyId,
                brandId: marcaIdRef.current,
                nombre,
                color,
                userId: usuarioIdRef.current,
              });
              return { id: m.id, nombre: m.nombre, cantidadFotos: 0 };
            },
            resolver: (pedidas, carpetas) =>
              resolverFuentesDeFoto(pedidas, {
                companyId: companyIdRef.current,
                userId: usuarioIdRef.current,
                productos: productosRef.current,
                modelos: modelosRef.current,
                // Las imágenes viajan en el MENSAJE, no en los chips (que se vacían al
                // enviar): se leen del último mensaje del usuario que las tenga
                // (ADR-2026-09-08-los-adjuntos-quedan-en-la-burbuja).
                adjuntas: ultimasImagenesAdjuntadas(motorRef.current?.messages ?? []).map((a) => ({
                  storagePath: a.storagePath,
                  bucket: a.bucket,
                })),
                carpetas,
              }),
            guardar: async (moodboardId, url) => {
              const companyId = companyIdRef.current;
              if (!companyId) throw new Error('sin empresa');
              await subirFotoDesdeUrl({ companyId, moodboardId, url });
            },
            refrescar: refrescarMoodboardsRef.current,
          }),
        [NOMBRE_EDITAR_MOODBOARD]: (input) =>
          resolverEditarMoodboard(input, {
            listar: listarCarpetas,
            renombrar: renombrarMoodboard,
            pintar: cambiarColorDeMoodboard,
            // El MISMO orden que numera `ver_moodboard`: si acá cambiara, el agente
            // borraría una foto distinta de la que miró.
            fotosDe: async (moodboardId) => {
              const companyId = companyIdRef.current;
              if (!companyId) return [];
              const fotos = await fetchFotos({ companyId, moodboardId });
              return fotos.map((f) => ({ id: f.id, path: f.path }));
            },
            sacar: (foto) => eliminarFoto({ fotoId: foto.id, path: foto.path }),
            refrescar: refrescarMoodboardsRef.current,
          }),
        // ── Las de pantalla (ADR-2026-09-10-el-agente-toca-la-pantalla) ──
        [NOMBRE_VER_PANTALLA]: () => {
          const c = puente.actual();
          return resultadoDeVerPantalla(
            c ? c.describir() : null,
            pantallaDeRuta(rutaRef.current),
            c?.herramientas ?? [],
          );
        },
        [NOMBRE_CONFIGURAR_PANTALLA]: async (input) => {
          const c = puente.actual();
          if (!c) return resultadoSinPantalla(pantallaDeRuta(rutaRef.current));
          // Se valida contra la ficha de ESTE momento: opciones, rangos, largos.
          const antes = c.describir();
          const { validos, rechazados } = validarCambios(antes, normalizarCambios(input));
          let aplicados: string[] = [];
          let fallidos: CambioRechazado[] = [];
          if (validos.length > 0) {
            const r = await c.aplicar(validos);
            aplicados = r.aplicados;
            fallidos = r.fallidos;
            await tick();
          }
          const despues = puente.actual()?.describir() ?? null;
          return resultadoDeConfigurar({
            aplicados: validos.filter((v) => aplicados.includes(v.campo)),
            rechazados,
            fallidos,
            despues,
          });
        },
        [NOMBRE_ABRIR_MODO]: async (input) => {
          const pedido = (input as { modo?: unknown } | null)?.modo;
          const resuelto = resolverModoAAbrir(pedido, ctxModoRef.current);
          if (!resuelto.ok) return resultadoDeAbrirModo({ resuelto });
          const { modo, kind, ruta } = resuelto;
          // Los modos con pantalla propia se registran con su path; los del sheet, por kind.
          const clave = modo.route ? modo.path : `modo:${kind}`;
          const tieneFicha = KINDS_CON_FICHA.includes(modo.route ? modo.path : kind);
          const ids = productIdsDelInput(input);
          const productos = ids
            .map((id) => productosRef.current.find((p) => p.id === id))
            .filter((p): p is (typeof productosRef.current)[number] => !!p);
          const noEncontrados = ids.filter((id) => !productos.some((p) => p.id === id));

          const actual = puente.actual();
          const yaEstaba = actual?.clave === clave;
          if (!yaEstaba) {
            // El modo vuelve al chat al salir (ADR-2026-09-16-volver-a-donde-vine).
            const volverA = { path: RUTA_CHAT_ATLAS };
            if (modo.route) navigateRef.current(ruta, { state: conVolverA({}, volverA) });
            else {
              // El mismo state con el que la app abre un modo desde el sheet de Generar:
              // `presetData` monta el sheet en el primer paint y los productos saltean el
              // paso de elegirlos (`useModosGenerar`).
              navigateRef.current(ruta, {
                state: conVolverA(
                  { presetData: modo.preset, collectionPresets: [], productosPreseleccionados: productos },
                  volverA,
                ),
              });
            }
          }
          if (!tieneFicha) {
            return resultadoDeAbrirModo({ resuelto, descripcion: null, yaEstaba, productosNoAplicados: noEncontrados });
          }
          const c = yaEstaba ? actual : await esperarPantalla(puente, clave, 5000);
          const noAplicados = [...noEncontrados];
          if (c && productos.length > 0) {
            // Por si la pantalla ya estaba montada (la navegación no re-lee el state):
            // los productos entran por el mismo camino validado que configurar_pantalla.
            const d = c.describir();
            const puestos = d.campos.find((x) => x.id === 'productos')?.valor;
            const yaPuestos =
              Array.isArray(puestos) && productos.every((p) => (puestos as string[]).includes(p.id));
            if (!yaPuestos) {
              const { validos, rechazados } = validarCambios(d, [
                { campo: 'productos', valor: productos.map((p) => p.id) },
              ]);
              if (validos.length > 0) await c.aplicar(validos);
              noAplicados.push(...rechazados.map((r) => r.motivo));
              await tick();
            }
          }
          return resultadoDeAbrirModo({
            resuelto,
            descripcion: puente.actual()?.describir() ?? null,
            yaEstaba,
            productosNoAplicados: noAplicados,
          });
        },
        [NOMBRE_HERRAMIENTA_GRAFICA]: (input, { adjuntarAlUltimoAssistant }) => {
          const g = normalizarGrafico(input);
          if (g) {
            adjuntarAlUltimoAssistant({
              graficos: [{ id: nuevoId(), titulo: g.titulo, html: g.html }],
            });
          }
          return resultadoHerramienta(NOMBRE_HERRAMIENTA_GRAFICA, input);
        },
        [NOMBRE_HERRAMIENTA_SUGERENCIAS]: (input, { adjuntarAlUltimoAssistant }) => {
          const s = normalizarSugerencias(input);
          if (s.length > 0) adjuntarAlUltimoAssistant({ sugerencias: s });
          return resultadoHerramienta(NOMBRE_HERRAMIENTA_SUGERENCIAS, input);
        },
        // ── configurar_preferencias (ADR-2026-09-13-el-agente-configura-las-preferencias).
        // Silenciosa a propósito: cambiar un default es reversible, instantáneo y gratis
        // — una card de confirmación ahí es ceremonia. Los permisos SÓLO se restauran;
        // no hay camino desde acá que los afloje.
        [NOMBRE_CONFIGURAR_PREFERENCIAS]: (input) =>
          resolverConfigurarPreferencias(input, {
            preferencias: () => preferenciasRef.current,
            guardarPreferencias: (p) => {
              // Campo por campo con el setter de siempre: es el que persiste y el que
              // hace que el menú "+" muestre lo nuevo sin recargar.
              (Object.keys(p) as Array<keyof PreferenciasDeGeneracion>).forEach((k) => {
                if (preferenciasRef.current[k] !== p[k]) setPreferenciaRef.current(k, p[k]);
              });
            },
            permisos: () => permisosRef.current,
            guardarPermisos: (p) => {
              (Object.keys(p) as Array<keyof PermisosChat>).forEach((k) => {
                if (permisosRef.current[k] !== p[k]) setPermisoRef.current(k, p[k]);
              });
            },
          }),
      },
      busquedaWeb: () => interruptoresRef.current.busquedaWeb,
      // `meta_ads` + `brand_id` si la marca tiene anuncios conectados y no es Baja.
      cuerpoExtra: () => cuerpoDeAnuncios(anunciosRef.current, marcaIdRef.current, inteligenciaRef.current),
      // El lote enganchado le llega al modelo como una línea del bloque de contexto del
      // mensaje: sabe que existe, con qué id, y qué herramienta lo toma.
      contextoAdjunto: () =>
        loteRef.current
          ? [
              lineaDeContextoDelLote({
                id: loteRef.current.id,
                archivos: { length: loteRef.current.cantidad },
              }),
            ]
          : [],
      // Y en la burbuja del usuario queda el chip "N fotos · Lote de productos".
      adjuntosDelMensaje: () =>
        loteRef.current
          ? [{ kind: 'lote', loteId: loteRef.current.id, cantidad: loteRef.current.cantidad }]
          : [],
      promptExtra: () => ({
        estable: reglasChatAtlas(),
        volatil: contextoVolatilChatAtlas({
          contexto: contextoRef.current,
          tienda: tiendaRef.current,
          instagram: instagramRef.current,
          // Se desconectó: lo vio conectado en esta sesión, o la charla tiene números de
          // Instagram de antes (sobrevive a recargar la página).
          instagramSeDesconecto:
            !!instagramRef.current &&
            !instagramRef.current.conectada &&
            (instagramVistoConectadoRef.current || leyoInstagramEnLaCharla(motorRef.current?.messages ?? [])),
          anuncios: anunciosRef.current,
          anunciosSeDesconectaron:
            !!anunciosRef.current && !anunciosRef.current.conectada && anunciosVistosConectadosRef.current,
          mercadolibre: mercadolibreRef.current,
          mercadolibreSeDesconecto:
            !!mercadolibreRef.current && !mercadolibreRef.current.conectada && mercadolibreVistoConectadoRef.current,
          inteligencia: inteligenciaRef.current,
          moodboards: moodboardsRef.current,
          interruptores: interruptoresRef.current,
          permisos: permisosRef.current,
          // La pantalla de AHORA, no la de cuando abrió el chat: se lee en cada envío.
          pantalla: (() => {
            const c = puente.actual();
            return {
              descripcion: c ? c.describir() : null,
              nombre: pantallaDeRuta(rutaRef.current),
              herramientas: c?.herramientas?.map((h) => h.name),
              skill: c?.skill,
            };
          })(),
        }),
      }),
      // Qué tool se resuelve sola. Se lee en cada turno: cambiar un permiso vale para el
      // pedido siguiente sin recargar.
      sinConfirmacion: (tool) => {
        const accion = accionDeTool(tool);
        return accion ? accionSinConfirmacion(permisosRef.current, accion) : false;
      },
    }),
    // `puente` es estable (lo memoiza su provider); todo lo demás entra por refs.
    [puente],
  );

  const motor = useV2Chat(entorno.shortName, entorno.productos, opciones);
  // Cuántas veces habló la persona. Igual que en el chat del canvas: es lo que distingue
  // "me dijo que sí" de "el modelo escribió confirmado".
  turnoRef.current = motor.messages.filter((m) => m.role === 'user').length;
  // Y QUÉ dijo la última vez. Que haya hablado no es que haya aceptado: el candado de lo
  // que no se deshace mira este texto, no lo que el modelo afirma
  // (ADR-2026-09-21-la-confirmacion-mira-lo-que-contesto-la-persona, review Greptile #127).
  ultimoMensajeRef.current =
    [...motor.messages].reverse().find((m) => m.role === 'user')?.text ?? '';
  // Espejo del motor para las funciones que se definen antes de él o que no quieren
  // recrearse con cada token (`abrirChat` es estable a propósito: lo llama el cascarón).
  const motorRef = useRef(motor);
  motorRef.current = motor;

  /**
   * Inteligencia → modelo del próximo envío, y persistida con clave propia (la del
   * asistente es otra: son dos preferencias).
   *
   * Al motor va el nivel EFECTIVO, recortado por plan
   * (ADR-2026-09-12-la-inteligencia-del-chat-es-de-pro-para-arriba): es el corte que
   * importa, porque el nivel vive en `localStorage` y una cuenta que puso Alta con plan
   * Pro y bajó a Básico lo conserva. Lo GUARDADO sigue siendo lo que el usuario eligió,
   * sin recortar: si vuelve a Pro, vuelve su nivel.
   */
  const puedeSubir = puedeSubirInteligencia(entorno.plan);
  const inteligenciaEfec = inteligenciaEfectiva(inteligencia, entorno.plan);
  inteligenciaRef.current = inteligenciaEfec;
  const setModel = motor.setModel;
  useEffect(() => {
    setModel(intelligenceToModel(inteligenciaEfec));
    guardarInteligencia(storageLocal(), inteligencia);
  }, [inteligencia, inteligenciaEfec, setModel]);
  const setInteligencia = useCallback((nivel: Intelligence) => setInteligenciaState(nivel), []);

  // El motor de imagen se persiste como la inteligencia: es una preferencia de la CUENTA,
  // no de la conversación — se elige una vez y vale para los chats que vengan.
  useEffect(() => {
    guardarModeloDeImagen(storageLocal(), modeloDeImagen);
  }, [modeloDeImagen]);
  const setModeloDeImagen = useCallback(
    (proveedor: ProveedorDeImagen | null) => setModeloDeImagenState(proveedor),
    [],
  );

  // El interruptor de investigación manda al motor: el usuario ya optó, así que no
  // pasa por la tarjeta de confirmación. Al montar se sincroniza con lo guardado.
  const setResearchEnabled = motor.setResearchEnabled;
  useEffect(() => {
    setResearchEnabled(interruptoresRef.current.investigacion);
    // Sólo al montar: después lo mueve `setInterruptor`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setInterruptor = useCallback(
    (clave: keyof InterruptoresChat, valor: boolean) => {
      setInterruptores((prev) => {
        const siguiente = { ...prev, [clave]: valor };
        guardarInterruptores(storageLocal(), siguiente);
        return siguiente;
      });
      if (clave === 'investigacion') setResearchEnabled(valor);
    },
    [setResearchEnabled],
  );

  const setPreferencia = useCallback(
    <K extends keyof PreferenciasDeGeneracion>(clave: K, valor: PreferenciasDeGeneracion[K]) => {
      setPreferencias((prev) => {
        // Pasa por `normalizarPreferencias` y no se guarda el objeto pelado porque hay
        // campos que dependen entre sí: bajar la calidad del video a Estándar recorta una
        // duración de 30 s al tope de 15
        // (ADR-2026-09-21-las-preferencias-salen-a-la-caja-como-pastillas). `guardar` ya
        // normalizaba, así que sin esto el estado vivo decía 30 s, el storage 15 s, y el
        // valor "cambiaba solo" al recargar.
        const siguiente = normalizarPreferencias({ ...prev, [clave]: valor });
        guardarPreferencias(storageLocal(), siguiente);
        return siguiente;
      });
    },
    [],
  );

  const setPermiso = useCallback(
    <K extends keyof PermisosChat>(clave: K, valor: PermisosChat[K]) => {
      setPermisosState((prev) => {
        const siguiente = { ...prev, [clave]: valor };
        guardarPermisos(storageLocal(), siguiente);
        return siguiente;
      });
    },
    [],
  );
  // Los espejos de arriba, ya con el setter de verdad: `opciones` se armó antes.
  setPermisoRef.current = setPermiso;
  setPreferenciaRef.current = setPreferencia;

  /**
   * EL CHAT DEL PROYECTO (ADR-2026-09-11-el-chat-del-proyecto-de-video). Abrir el chat
   * adentro del editor de video trae el último chat de ese proyecto, como hacía el
   * Director con su hilo por proyecto. Si el proyecto no tiene ninguno y la conversación
   * actual ya tiene mensajes, arranca una nueva: al crearse queda asociada al proyecto
   * (`opciones.proyecto` lee la ruta en ese momento). Con un turno en curso no se cambia
   * de chat: cortaría el stream.
   */
  const irAlChatDelProyecto = useCallback(
    async (proyecto: string) => {
      if (motorRef.current.streaming) return;
      try {
        const { buscarChatDelProyecto } = await import('../pages/v2/agentChatApi');
        const id = await buscarChatDelProyecto(proyecto);
        const m = motorRef.current;
        if (m.streaming) return;
        if (id) {
          if (id !== m.getChatId()) {
            setGraficoActivoId(null);
            await m.loadChatById(id);
          }
          return;
        }
        if (m.messages.length > 0) {
          m.reset();
          setGraficoActivoId(null);
          quitarLoteFotos();
        }
      } catch (e) {
        console.error('[ChatAtlas] chat del proyecto', e);
      }
    },
    [quitarLoteFotos],
  );

  const abrirChat = useCallback(
    (op?: OpcionesApertura) => {
      if (op?.contexto !== undefined) setContexto(op.contexto);
      if (op?.vista) setVista(op.vista);
      if (op?.mensajeInicial !== undefined) setBorrador(op.mensajeInicial);
      setAbierto(true);
      const proyecto = proyectoDeRuta(rutaRef.current);
      if (proyecto) void irAlChatDelProyecto(proyecto);
    },
    [irAlChatDelProyecto],
  );

  // Cerrar NO borra la conversación: al reabrir el chat sigue donde estaba.
  const cerrarChat = useCallback(() => setAbierto(false), []);
  const cambiarVista = useCallback((v: VistaChat) => setVista(v), []);

  const entrarAPantalla = useCallback(({ conservarContexto }: EntradaAPantalla) => {
    setVista('pantalla');
    if (conservarContexto) {
      setAbierto(true);
      return;
    }
    setContexto(null);
    setAbierto(false);
  }, []);

  const abrirGrafico = useCallback((id: string) => setGraficoActivoId(id), []);
  const cerrarGrafico = useCallback(() => setGraficoActivoId(null), []);

  const sendMessage = motor.sendMessage;
  const enviarMensaje = useCallback(
    (texto: string) => {
      // Una propuesta de generación esperando NO frena el envío: el motor la suelta
      // (queda en el chat, clickeable) y sigue con lo nuevo — como el asistente.
      if (motor.activeToolCard) motor.detachTurn();
      sendMessage(texto);
      setBorrador('');
      // El lote se consumió: pasa a ser de la tarjeta (que lo libera al terminar). Acá
      // sólo se suelta el chip — NO se libera el registro.
      setLoteFotos(null);
    },
    [motor, sendMessage],
  );

  const encolarMensaje = useCallback(
    (texto: string): boolean => {
      // Los adjuntos se CAPTURAN con el mensaje y se van de la caja, igual que al
      // enviar: si se quedaran ahí, cada mensaje de la cola saldría con los chips que
      // hubiera en su momento y no con los suyos
      // (ADR-2026-09-13-la-cola-lleva-sus-adjuntos).
      const r = encolar(colaRef.current, {
        texto,
        // `tentativasRef` se declara más abajo (sale de la selección del lienzo); acá se
        // lee al encolar, cuando ya tiene valor.
        adjuntos: adjuntosAlMandar(texto, motorRef.current.attachments, tentativasRef.current),
        loteFotos: loteRef.current,
      });
      if (!r.encolado) return false;
      colaRef.current = r.cola;
      setCola(r.cola);
      setBorrador('');
      // Sin liberar nada: los bytes del PDF, la planilla o el lote tienen que seguir
      // vivos hasta que el mensaje salga de la cola.
      motorRef.current.reemplazarAdjuntos([]);
      loteRef.current = null;
      setLoteFotos(null);
      return true;
    },
    [],
  );

  /** Un mensaje que se descarta sin mandarse suelta sus documentos en memoria: ningún
   *  mensaje del chat los va a referenciar, así que se sueltan acá o no se sueltan nunca
   *  (mismo criterio que `removeAttachment` en el motor). */
  const soltarDocumentosDe = useCallback((m: MensajeEnCola) => {
    for (const a of m.adjuntos) {
      if (a.kind === 'pdf') liberarPdf(a.pdfId);
      else if (a.kind === 'planilla') liberarPlanilla(a.planillaId);
    }
    if (m.loteFotos) liberarLoteDeFotos(m.loteFotos.id);
  }, []);

  /** Chat nuevo o abrir otro del historial: la cola de este no aplica, y lo que estaba
   *  esperando nunca se va a mandar. */
  const vaciarCola = useCallback(() => {
    for (const m of colaRef.current) soltarDocumentosDe(m);
    colaRef.current = [];
    setCola([]);
  }, [soltarDocumentosDe]);

  const quitarDeCola = useCallback(
    (indice: number) => {
      const fuera = colaRef.current[indice];
      if (fuera) soltarDocumentosDe(fuera);
      setCola((prev) => sacarDeCola(prev, indice));
    },
    [soltarDocumentosDe],
  );

  // El agente quedó libre y hay cola: sale el primero. Un solo mensaje por vez; el
  // siguiente espera a que este turno termine, como si lo hubiera tipeado recién.
  useEffect(() => {
    if (motor.streaming || cola.length === 0) return;
    const s = siguienteDeCola(cola);
    if (!s) return;
    setCola(s.resto);
    // Los adjuntos vuelven a la caja JUSTO antes de mandar, en el mismo tick: por eso
    // `reemplazarAdjuntos` escribe el ref y no sólo el state — `sendMessage` lee el ref.
    motorRef.current.reemplazarAdjuntos(s.mensaje.adjuntos);
    loteRef.current = s.mensaje.loteFotos;
    setLoteFotos(s.mensaje.loteFotos);
    enviarMensaje(s.mensaje.texto);
  }, [motor.streaming, cola, enviarMensaje]);

  // Detener: el usuario está interviniendo, así que la cola no sale sola. Los textos
  // vuelven a la caja, debajo de lo que hubiera, y él decide qué mandar.
  const detenerMotor = motor.stopTurn;
  const detener = useCallback(() => {
    detenerMotor();
    const pendientes = colaRef.current;
    if (pendientes.length > 0) {
      colaRef.current = [];
      setCola([]);
      setBorrador((b) => devolverAlBorrador(b, pendientes));
      // Y los adjuntos con ellos: se juntan en una sola caja (se pierde a qué mensaje
      // era cada uno, no el archivo, que es lo que no se puede rehacer).
      const enLaCaja = motorRef.current.attachments;
      const vuelven = adjuntosDeLaCola(pendientes, enLaCaja);
      if (vuelven.length > 0) motorRef.current.reemplazarAdjuntos([...enLaCaja, ...vuelven]);
      const lote = loteRef.current ?? primerLoteDeLaCola(pendientes);
      if (lote !== loteRef.current) {
        loteRef.current = lote;
        setLoteFotos(lote);
      }
    }
  }, [detenerMotor]);

  // Chat nuevo: la fila actual queda en `agent_chats` como historial; se van el
  // borrador, los adjuntos enganchados (si no, el primer mensaje del chat "nuevo" se
  // llevaba puesta la foto del anterior) y el gráfico abierto, cuyos mensajes ya no
  // existen.
  const reset = motor.reset;
  const reiniciar = useCallback(() => {
    reset();
    // Un aviso de cobro (1080p, tienda) es de ESTA conversación: el contador de turnos
    // vuelve a empezar en la otra, y un "sí" de allá no puede habilitarlo.
    olvidarAvisos();
    setGraficoActivoId(null);
    setBorrador('');
    vaciarCola();
    quitarLoteFotos();
  }, [reset, quitarLoteFotos, vaciarCola]);

  const loadChatById = motor.loadChatById;
  const cargarChat = useCallback(
    async (chatId: string) => {
      setGraficoActivoId(null);
      vaciarCola();
      olvidarAvisos(); // ver `reiniciar`: los avisos no cruzan de conversación.
      await loadChatById(chatId);
    },
    [loadChatById, vaciarCola],
  );

  const cascaron = useMemo<CascaronChatAtlas>(
    () => ({ abierto, vista, contexto, abrirChat, cerrarChat, cambiarVista, entrarAPantalla }),
    [abierto, vista, contexto, abrirChat, cerrarChat, cambiarVista, entrarAPantalla],
  );

  // ── La selección del lienzo en la caja (ADR-2026-09-24-la-seleccion-del-lienzo-se-
  // adjunta-al-escribir). Lo seleccionado aparece apagado; escribir lo adjunta de verdad.
  // `descartadas` son las que el usuario sacó con la X mientras seguían seleccionadas: no
  // vuelven apagadas hasta que la selección cambie.
  const [seleccion, setSeleccion] = useState<SeleccionDelLienzo>(SIN_SELECCION);
  // React Flow informa la selección más veces de las que cambia: con la misma, se
  // conserva el objeto para que el valor del contexto no se rehaga en vano.
  const fijarSeleccionDelLienzo = useCallback((s: SeleccionDelLienzo) => {
    setSeleccion((prev) =>
      prev.ids.join('|') === s.ids.join('|') && prev.chips.map((c) => c.id).join('|') === s.chips.map((c) => c.id).join('|')
        ? prev
        : s,
    );
  }, []);
  const [descartadas, setDescartadas] = useState<ReadonlySet<string>>(() => new Set());
  const claveSeleccion = seleccion.ids.join('|');
  useEffect(() => {
    setDescartadas((d) => (d.size === 0 ? d : new Set()));
  }, [claveSeleccion]);
  const adjuntosTentativos = useMemo(
    () => tentativasDeSeleccion(seleccion.chips, motor.attachments, descartadas),
    [seleccion.chips, motor.attachments, descartadas],
  );
  const tentativasRef = useRef<ChatAttachment[]>([]);
  tentativasRef.current = adjuntosTentativos;
  const escribirBorrador = useCallback(
    (texto: string) => {
      setBorrador(texto);
      // Cualquier cambio que deje texto en la caja (tipeo, dictado, una sugerencia)
      // convierte las tentativas en adjuntos: el usuario "aceptó" lo seleccionado.
      if (texto.trim()) for (const a of tentativasRef.current) motorRef.current.addAttachment(a);
    },
    [],
  );
  const quitarAdjunto = useCallback(
    (id: string) => {
      if (seleccion.chips.some((c) => c.id === id)) setDescartadas((d) => new Set([...d, id]));
      motorRef.current.removeAttachment(id);
    },
    [seleccion.chips],
  );
  // Mandar con texto también acepta las tentativas (ver `adjuntosAlMandar`). Va en el
  // envío del usuario y NO en `enviarMensaje` a secas: ése también lo usa la cola, y un
  // mensaje encolado sale con SUS adjuntos, no con lo que esté seleccionado cuando le toca
  // salir.
  const enviarDesdeLaCaja = useCallback(
    (texto: string) => {
      const enLaCaja = motorRef.current.attachments;
      const adjuntos = adjuntosAlMandar(texto, enLaCaja, tentativasRef.current);
      // `reemplazarAdjuntos` escribe el ref además del state: `sendMessage` lee el ref, en
      // este mismo tick.
      if (adjuntos !== enLaCaja) motorRef.current.reemplazarAdjuntos(adjuntos);
      enviarMensaje(texto);
    },
    [enviarMensaje],
  );

  const valor = useMemo<ValorChatAtlas>(
    () => ({
      ...cascaron,
      borrador,
      setBorrador: escribirBorrador,
      adjuntos: motor.attachments,
      agregarAdjunto: motor.addAttachment,
      quitarAdjunto,
      adjuntosTentativos,
      seleccionDelLienzo: seleccion.ids,
      fijarSeleccionDelLienzo,
      loteFotos,
      refrescarMoodboards,
      agregarLoteFotos,
      quitarLoteFotos,
      graficoActivoId,
      abrirGrafico,
      cerrarGrafico,
      mensajes: motor.messages,
      streaming: motor.streaming,
      actividad: motor.toolActivity,
      interruptores,
      setInterruptor,
      preferencias,
      setPreferencia,
      inteligencia: inteligenciaEfec,
      puedeSubirInteligencia: puedeSubir,
      setInteligencia,
      modeloDeImagen,
      setModeloDeImagen,
      permisos,
      setPermiso,
      enviarMensaje: enviarDesdeLaCaja,
      encolarMensaje,
      quitarDeCola,
      cola,
      detener,
      reiniciar,
      cargarChat,
      fetchConsumo: motor.fetchConsumo,
      motor,
    }),
    [
      cascaron,
      borrador,
      escribirBorrador,
      quitarAdjunto,
      adjuntosTentativos,
      seleccion.ids,
      fijarSeleccionDelLienzo,
      loteFotos,
      refrescarMoodboards,
      agregarLoteFotos,
      quitarLoteFotos,
      graficoActivoId,
      abrirGrafico,
      cerrarGrafico,
      motor,
      interruptores,
      setInterruptor,
      preferencias,
      setPreferencia,
      inteligenciaEfec,
      puedeSubir,
      setInteligencia,
      modeloDeImagen,
      setModeloDeImagen,
      isDevTester,
      permisos,
      setPermiso,
      enviarDesdeLaCaja,
      encolarMensaje,
      quitarDeCola,
      cola,
      reiniciar,
      cargarChat,
    ],
  );

  return (
    <CascaronContext.Provider value={cascaron}>
      <ChatAtlasContext.Provider value={valor}>
        {/* Lo que las tarjetas que generan leen para mandar `provider_override`
            (ADR-2026-09-12-gpt-image-2-5-en-el-harness-y-en-el-agente). */}
        <ProveedorDeImagenContext.Provider value={modeloDeImagen}>
          {children}
        </ProveedorDeImagenContext.Provider>
      </ChatAtlasContext.Provider>
    </CascaronContext.Provider>
  );
};

/** Estado del chat de Atlas. Tira error si falta el provider (mejor eso que un botón
 *  que no hace nada). */
export const useChatAtlas = (): ValorChatAtlas => {
  const ctx = useContext(ChatAtlasContext);
  if (!ctx) {
    throw new Error('useChatAtlas necesita estar dentro de <ChatAtlasProvider>');
  }
  return ctx;
};

/**
 * Sólo la parte de apertura/layout. ES LO QUE TIENE QUE USAR EL CASCARÓN
 * (`useEmpujeChat` → `DashboardLayout`): leer el contexto grande desde ahí arriba
 * re-renderiza la sidebar entera con cada token del stream.
 */
export const useChatAtlasCascaron = (): CascaronChatAtlas => {
  const ctx = useContext(CascaronContext);
  if (!ctx) {
    throw new Error('useChatAtlasCascaron necesita estar dentro de <ChatAtlasProvider>');
  }
  return ctx;
};
